import React, { useEffect, useRef, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import type {
  TChatRating,
  TChatRatingReason,
  TChatSource,
  TChatSummaryDto
} from '@homepal/api-contract'
import { TMetricView } from 'types/GlobalMetric'
import { Action, Context } from 'types/GlobalUserEvents'
import { SnackbarMessage } from 'types/GlobalSnackbar'

import {
  getChat,
  getChats,
  prefetchStreamAuth,
  rateMessage,
  streamChat
} from 'redux/api/internal/Chat'

import { useSnackbarActions } from 'redux/actions/Snackbar'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTheme } from '@mui/material'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Collapse from 'components_new/atoms/Collapse'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import ChatFeedbackPopover from 'components_new/molecules/ChatFeedbackPopover'
import ChatInput from 'components_new/molecules/ChatInput'
import MarkdownContent from 'components_new/molecules/MarkdownContent'

import Avatar from './Avatar'
import ChatHistoryLink from './ChatHistoryLink'
import ChatHistoryList from './ChatHistoryList'
import FeedbackNudge from './FeedbackNudge'
import ResumedChatHeader from './ResumedChatHeader'
import ThinkingSummary from './ThinkingSummary'
import WorkingStatus from './WorkingStatus'
import { toMessages } from './hydrate'
import { Message, TOpenedChat } from './types'
import { useFeedbackNudge } from './useFeedbackNudge'

interface ChatBoxProps {
  // rowCount is only the first rows; more match the selection
  hasMoreRows: boolean
  historyEnabled: boolean
  metricView: TMetricView
  metricTitle: string
  // null while the table loads the rows of the current selection
  rowCount: number | null
  source: TChatSource
  // The analysis or kpi option the chat is anchored to.
  sourceId?: string
  onExpandedChange?: (expanded: boolean) => void
  // Emits a user event through the host dialog, which owns the entry-point
  // context and the dashboard/analysis/report ids.
  onLogEvent: (action: Action, input?: string | null, context?: Context) => void
}

const ChatBox = (props: ChatBoxProps) => {
  const {
    hasMoreRows,
    historyEnabled,
    metricView,
    metricTitle,
    rowCount,
    source,
    sourceId,
    onExpandedChange,
    onLogEvent
  } = props

  const { t } = useTranslation()
  const theme = useTheme()
  const { account } = useAccountSettings()
  const { toggleSnackbarOpen } = useSnackbarActions()

  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chatId, setChatId] = useState<string | undefined>(undefined)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [feedbackFor, setFeedbackFor] = useState<Message | null>(null)
  const [feedbackAnchor, setFeedbackAnchor] = useState<HTMLElement | null>(null)
  const [feedbackFromNudge, setFeedbackFromNudge] = useState(false)
  const [history, setHistory] = useState<TChatSummaryDto[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [browsingHistory, setBrowsingHistory] = useState(false)
  const [historyOpenCount, setHistoryOpenCount] = useState(0)
  // A chat is on its way in; the panel shows it before it has content.
  const [openingChat, setOpeningChat] = useState(false)
  const [openedChat, setOpenedChat] = useState<TOpenedChat | null>(null)

  const idRef = useRef(0)
  // An earlier response that lands after this one is dropped.
  const resumeRef = useRef<string | null>(null)
  // A chat opened from history, until its first new message is sent.
  const resumedChatIdRef = useRef<string | null>(null)
  // A question carried over from "ask again", sent once the panel has reset.
  const pendingQuestionRef = useRef<string | null>(null)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  // The input is disabled while streaming, which drops focus on submit —
  // hand it back once the input is enabled again.
  useEffect(() => {
    if (!streaming) inputRef.current?.focus()
  }, [streaming])

  const starters = [
    t('chat', 'starter_deviations'),
    t('chat', 'starter_concentration')
  ]

  useEffect(() => {
    prefetchStreamAuth()

    return () => {
      abortRef.current?.abort()

      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    }
  }, [])

  // The dialog is wide while a chat with messages is shown.
  const expanded = !browsingHistory && messages.length > 0

  useEffect(() => {
    onExpandedChange?.(expanded)
  }, [expanded, onExpandedChange])

  const viewKey = JSON.stringify(metricView)
  const historySourceId = historyEnabled ? sourceId : undefined

  // Reloaded whenever the panel returns to its empty state.
  useEffect(() => {
    if (!historySourceId || openingChat || (chatId && !browsingHistory)) return

    let active = true

    setLoadingHistory(true)

    getChats({ source, source_id: historySourceId, metric_view: metricView })
      .then((response) => {
        if (active) setHistory(response.data.data)
      })
      .catch((e) => {
        console.error('Failed to load chat history:', e)

        if (active) setHistory([])
      })
      .finally(() => {
        if (active) setLoadingHistory(false)
      })

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, historySourceId, viewKey, chatId, browsingHistory, openingChat])

  // Replays a past chat. The dialog keeps its own filters and period.
  const resume = (id: string) => {
    abortRef.current?.abort()
    resumeRef.current = id
    setMessages([])
    setSuggestions([])
    setOpenedChat(null)
    setChatId(undefined)
    setError(null)
    setBrowsingHistory(false)
    setOpeningChat(true)

    getChat(id)
      .then((response) => {
        if (resumeRef.current !== id) return

        const chat = response.data.data
        const hydrated = toMessages(chat, () => (idRef.current += 1))

        setMessages(hydrated.messages)
        setSuggestions(hydrated.suggestions)
        setOpenedChat({
          title: chat.title,
          created_at: chat.created_at,
          row_count: chat.row_count,
          has_more_rows: false,
          read_only: chat.read_only,
          filters: chat.filters
        })
        setChatId(chat.id)
        setStreaming(false)
        resumedChatIdRef.current = chat.id

        onLogEvent(
          Action.AI_CHAT_HISTORY_CHAT_OPENED,
          chat.id,
          chat.read_only ? Context.CHAT_READ_ONLY : Context.CHAT_CONTINUABLE
        )
      })
      .catch((e) => {
        if (resumeRef.current !== id) return

        console.error('Failed to open chat:', e)
        setError(t('chat', 'history_open_failed'))
      })
      .finally(() => {
        if (resumeRef.current === id) setOpeningChat(false)
      })
  }

  // Leaving a chat also drops a load still on its way in.
  const showHistory = (origin: Context) => {
    resumeRef.current = null
    resumedChatIdRef.current = null
    setOpeningChat(false)
    setBrowsingHistory(true)
    setHistoryOpenCount((count) => count + 1)

    onLogEvent(Action.AI_CHAT_HISTORY_OPENED, null, origin)
  }

  // Back to the empty panel, which reloads the history list.
  const startNewChat = () => {
    abortRef.current?.abort()
    resumeRef.current = null
    resumedChatIdRef.current = null
    setMessages([])
    setSuggestions([])
    setOpenedChat(null)
    setOpeningChat(false)
    setChatId(undefined)
    setError(null)
    setStreaming(false)
    setBrowsingHistory(false)
  }

  // Puts the chat's opening question to the data as it stands now.
  const askAgain = () => {
    const question = messages.find((m) => m.role === 'user')?.content

    onLogEvent(Action.AI_CHAT_ASKED_AGAIN, chatId ?? null)

    startNewChat()

    if (question) pendingQuestionRef.current = question
  }

  // Aborts the stream and keeps whatever has been received so far.
  const stop = () => {
    abortRef.current?.abort()
    setMessages((prev) =>
      prev
        .filter((m) => !(m.pending && m.content.length === 0))
        .map((m) => (m.pending ? { ...m, pending: false } : m))
    )
    setStreaming(false)
  }

  const handleCopy = (message: Message) => {
    const markdown = message.content

    const markCopied = () => {
      setCopiedId(message.id)

      onLogEvent(Action.AI_CHAT_ANSWER_COPIED)

      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)

      copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 2000)
    }

    const onError = (err: unknown) =>
      console.error('Failed to copy message:', err)

    const writePlain = () =>
      navigator.clipboard.writeText(markdown).then(markCopied).catch(onError)

    // Like Claude's web chat, put both a rendered-HTML flavor and the raw
    // markdown on the clipboard, so rich-text targets (Docs, Notes) paste
    // formatted content while plain targets get the markdown source. Falls
    // back to plain text on browsers without ClipboardItem support.
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard.write) {
      writePlain()

      return
    }

    const html = ReactDOMServer.renderToStaticMarkup(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    )

    const item = new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([markdown], { type: 'text/plain' })
    })

    navigator.clipboard.write([item]).then(markCopied).catch(writePlain)
  }

  const setRating = (id: number, rating: TChatRating | null) =>
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, rating } : m)))

  // Feedback is write-once: the backend rejects a second rating, so a message
  // that already carries one is left alone rather than re-sent.
  //
  // The thumb colours immediately, but a lost call must not leave it coloured:
  // the buttons lock once rated, so the user would have no way to try again and
  // no way to tell the rating never landed.
  const submitRating = (
    message: Message,
    rating: TChatRating,
    reason: TChatRatingReason | null = null,
    comment: string | null = null,
    fromNudge = false
  ) => {
    const { messageId } = message

    if (!chatId || !messageId || message.rating) return

    setRating(message.id, rating)

    rateMessage(chatId, messageId, {
      rating,
      rating_reason: reason,
      rating_comment: comment
    })
      .then(() => {
        if (!fromNudge) toggleSnackbarOpen(SnackbarMessage.ChatFeedbackSent)
      })
      .catch((e) => {
        console.error('Failed to rate message:', e)

        // A 409 means the rating is already recorded, so the thumb is right.
        if (e?.response?.status === 409) return

        setRating(message.id, null)
        toggleSnackbarOpen(SnackbarMessage.ChatFeedbackSentFailed)
      })
  }

  const handleThumbDown = (
    message: Message,
    event: React.MouseEvent<HTMLElement>,
    fromNudge = false
  ) => {
    setFeedbackFor(message)
    setFeedbackAnchor(event.currentTarget)
    setFeedbackFromNudge(fromNudge)
  }

  const closeFeedback = () => {
    setFeedbackFor(null)
    setFeedbackAnchor(null)
    setFeedbackFromNudge(false)
  }

  const { target: nudgeTarget, dismiss: dismissNudge } = useFeedbackNudge(
    messages,
    streaming
  )

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages])

  const send = (text: string) => {
    const trimmed = text.trim()

    if (!trimmed || streaming || rowCount === null || openedChat?.read_only) {
      return
    }

    if (resumedChatIdRef.current) {
      onLogEvent(
        Action.AI_CHAT_HISTORY_CHAT_CONTINUED,
        resumedChatIdRef.current
      )
      resumedChatIdRef.current = null
    }

    if (nudgeTarget?.rating) dismissNudge()

    const userId = (idRef.current += 1)
    const assistantId = (idRef.current += 1)

    const patch = (changes: Partial<Message>) =>
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, ...changes } : m))
      )

    setMessages((prev) => [
      ...prev,
      { id: userId, role: 'user', content: trimmed },
      { id: assistantId, role: 'assistant', content: '', pending: true }
    ])
    setInput('')
    setError(null)
    setSuggestions([])
    setStreaming(true)

    const controller = new AbortController()
    let finished = false
    let streamChatId = chatId

    abortRef.current = controller

    streamChat(
      {
        message: trimmed,
        chat_id: chatId,
        metric_view: { ...metricView, row_count: rowCount },
        source,
        source_id: sourceId
      },
      {
        onStart: (data) => {
          streamChatId = data.chat_id
          setChatId(data.chat_id)
          setOpenedChat(
            (prev) =>
              prev ?? {
                title: data.title,
                created_at: null,
                row_count: rowCount,
                has_more_rows: hasMoreRows,
                read_only: false,
                filters: null
              }
          )
        },
        onToolStart: (data) =>
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, currentTool: data.tool } : m
            )
          ),
        onToolEnd: (step) =>
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    currentTool: null,
                    thinkingSteps: [...(m.thinkingSteps ?? []), step]
                  }
                : m
            )
          ),
        onText: (delta) =>
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: m.content + delta } : m
            )
          ),
        onReasoning: (delta) =>
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    currentTool: null,
                    reasoningText: (m.reasoningText ?? '') + delta
                  }
                : m
            )
          ),
        onDone: (data) => {
          finished = true
          setChatId(data.chat_id)
          setSuggestions(data.suggestions ?? [])
          patch({
            messageId: data.message_id,
            content: data.reply,
            reasoningText: data.reasoning_text ?? undefined,
            thinkingSteps: data.thinking_steps,
            durationMs: data.duration_ms,
            pending: false
          })
        },
        onError: (message) => {
          finished = true
          setError(message)
          setMessages((prev) => prev.filter((m) => m.id !== assistantId))
        },
        // The chat turned read-only between opening and sending: the message
        // goes back into the input.
        onReadOnly: () => {
          finished = true
          setMessages((prev) =>
            prev.filter((m) => m.id !== assistantId && m.id !== userId)
          )
          setInput(trimmed)
          setOpenedChat((prev) => (prev ? { ...prev, read_only: true } : prev))
        }
      },
      controller.signal
    )
      .then(() => {
        // The stream closed without a done event: the backend died mid-reply.
        if (finished || controller.signal.aborted) return

        onLogEvent(Action.AI_CHAT_STREAM_INTERRUPTED, streamChatId ?? null)
        setError(t('chat', 'stream_interrupted'))
        setMessages((prev) =>
          prev
            .filter((m) => !(m.id === assistantId && m.content.length === 0))
            .map((m) => (m.id === assistantId ? { ...m, pending: false } : m))
        )
      })
      .catch((e) => {
        if (controller.signal.aborted) return

        setError(e instanceof Error ? e.message : String(e))
        setMessages((prev) => prev.filter((m) => m.id !== assistantId))
      })
      .finally(() => {
        if (!controller.signal.aborted) setStreaming(false)
      })
  }

  useEffect(() => {
    const question = pendingQuestionRef.current

    if (!question || chatId || streaming || messages.length > 0) return

    pendingQuestionRef.current = null
    send(question)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, streaming, messages.length])

  const isEmpty = messages.length === 0
  const showStartScreen = isEmpty && !openingChat
  const lastMessage = messages[messages.length - 1]
  const readOnly = openedChat?.read_only ?? false

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {showStartScreen && historySourceId ? (
        <ChatHistoryLink
          count={history.length}
          onShowAll={() => showHistory(Context.FROM_CHAT_START)}
        />
      ) : null}

      {!showStartScreen && historySourceId ? (
        <ResumedChatHeader
          chat={openedChat}
          onAskAgain={askAgain}
          onBack={() => showHistory(Context.FROM_CHAT)}
          viewFilters={metricView.filters}
        />
      ) : null}

      {/* Message list */}
      <Box
        ref={listRef}
        sx={{
          display: 'flex',
          flex: showStartScreen ? 0 : 1,
          flexDirection: 'column',
          gap: 2,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          py: 2,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
          },
          '&::-webkit-scrollbar-corner': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.disabled',
            borderRadius: 2.5,
            outline: 'none'
          }
        }}
      >
        {openingChat && isEmpty ? (
          <>
            <Skeleton height={24} width="55%" sx={{ alignSelf: 'flex-end' }} />
            <Skeleton height={80} />
          </>
        ) : null}

        {messages.map((message) => {
          if (message.role === 'user') {
            return (
              <Box
                key={message.id}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <Box
                  sx={{
                    bgcolor: 'action.selected',
                    borderRadius: 2,
                    maxWidth: '90%',
                    px: 1.5,
                    py: 1
                  }}
                >
                  <Text variant="body1">{message.content}</Text>
                </Box>
              </Box>
            )
          }

          const showWorkingStatus =
            message.pending &&
            (message.content.length === 0 || Boolean(message.currentTool))
          const isLastMessage = message.id === lastMessage.id

          return (
            <Box
              key={message.id}
              sx={{ minWidth: 0, pb: isLastMessage ? 8 : 4 }}
            >
              {!message.pending || message.content.length > 0 ? (
                <ThinkingSummary
                  durationMs={message.durationMs}
                  reasoningText={message.reasoningText}
                  steps={message.thinkingSteps ?? []}
                />
              ) : null}

              {message.content.length > 0 ? (
                <>
                  <MarkdownContent>{message.content}</MarkdownContent>
                  {!message.pending ? (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        mt: 1
                      }}
                    >
                      <Tooltip
                        title={t(
                          'global',
                          copiedId === message.id ? 'copied' : 'copy'
                        )}
                      >
                        <IconButton
                          edge="start"
                          onClick={() => handleCopy(message)}
                          size="small"
                        >
                          <Icon
                            fontSize="small"
                            name={copiedId === message.id ? 'Check' : 'Copy'}
                          />
                        </IconButton>
                      </Tooltip>

                      {message.messageId ? (
                        <>
                          <Tooltip title={t('chat', 'feedback_helpful')}>
                            <IconButton
                              disabled={Boolean(message.rating)}
                              onClick={() => submitRating(message, 'up')}
                              size="small"
                            >
                              <Icon
                                color={
                                  message.rating === 'up'
                                    ? 'primary'
                                    : 'inherit'
                                }
                                fontSize="small"
                                name={
                                  message.rating === 'up'
                                    ? 'ThumbUpAlt'
                                    : 'ThumbUpAltOutlined'
                                }
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('chat', 'feedback_not_helpful')}>
                            <IconButton
                              disabled={Boolean(message.rating)}
                              onClick={(event) =>
                                handleThumbDown(message, event)
                              }
                              size="small"
                            >
                              <Icon
                                color={
                                  message.rating === 'down'
                                    ? 'primary'
                                    : 'inherit'
                                }
                                fontSize="small"
                                name={
                                  message.rating === 'down'
                                    ? 'ThumbDownAlt'
                                    : 'ThumbDownAltOutlined'
                                }
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}

                      {openedChat && openedChat.row_count !== null ? (
                        <>
                          <Divider flexItem={true} orientation="vertical" />

                          <Text color="text.secondary" variant="caption">
                            {t('chat', 'generated_from', false, {
                              count: openedChat.has_more_rows
                                ? `${openedChat.row_count}+`
                                : String(openedChat.row_count),
                              title: metricTitle
                            })}
                          </Text>
                        </>
                      ) : null}
                    </Box>
                  ) : null}
                </>
              ) : null}

              <Box
                sx={{ alignItems: 'center', display: 'flex', gap: 1, mt: 3 }}
              >
                {isLastMessage ? (
                  <Avatar
                    active={message.pending}
                    size={!showWorkingStatus ? 40 : 32}
                  />
                ) : null}

                {showWorkingStatus ? (
                  <WorkingStatus
                    currentTool={message.currentTool}
                    reasoningText={message.reasoningText}
                  />
                ) : null}
              </Box>
            </Box>
          )
        })}

        {nudgeTarget ? (
          <FeedbackNudge
            onClose={dismissNudge}
            onRateDown={(event) => handleThumbDown(nudgeTarget, event, true)}
            onRateUp={() => submitRating(nudgeTarget, 'up', null, null, true)}
            rated={Boolean(nudgeTarget.rating)}
          />
        ) : null}

        {error ? (
          <Text color="error" variant="body2">
            {error}
          </Text>
        ) : null}
      </Box>

      {/* Input */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flex: showStartScreen ? 1 : undefined,
          justifyContent: 'center',
          pb: showStartScreen ? 10 : undefined
        }}
      >
        {showStartScreen ? (
          <Box
            sx={{
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Tooltip title={t('chat', 'beta_tooltip')}>
                <Chip color="primary" label={t('chat', 'beta')} size="small" />
              </Tooltip>
            </Box>

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Avatar size={48} />
              <Text color="text.primary" variant="h4">
                {account
                  ? `${t('global', 'hi')}, ${account?.first_name}`
                  : `${t('global', 'hi')}!`}
              </Text>
            </Box>

            <Text align="center" color="text.secondary" variant="body1">
              {t('chat', 'greeting')}
            </Text>
          </Box>
        ) : null}

        {!isEmpty && !readOnly && suggestions ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {suggestions.map((suggestion) => (
                <Chip
                  clickable
                  color="default"
                  disabled={streaming || rowCount === null}
                  key={suggestion}
                  label={suggestion}
                  onClick={() => {
                    onLogEvent(
                      Action.AI_CHAT_SUGGESTION_CLICKED,
                      suggestion,
                      Context.FROM_SUGGESTIONS
                    )

                    send(suggestion)
                  }}
                  size="small"
                />
              ))}
            </Box>
            <Box
              sx={{
                background: `linear-gradient(to left, ${theme.palette.background.default}, transparent)`,
                bottom: 0,
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 0,
                width: 32
              }}
            />
          </Box>
        ) : null}

        <ChatInput
          autoFocus={true}
          disabled={streaming || readOnly}
          inputRef={inputRef}
          multiline={true}
          onChange={setInput}
          onStop={streaming ? stop : undefined}
          onSubmit={() => send(input)}
          placeholder={t(
            'chat',
            readOnly ? 'input_placeholder_stale' : 'input_placeholder'
          )}
          value={input}
        />

        {showStartScreen ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'center',
              my: 1
            }}
          >
            {starters.map((suggestion) => (
              <Chip
                clickable
                disabled={streaming || rowCount === null}
                key={suggestion}
                label={suggestion}
                onClick={() => send(suggestion)}
                size="small"
              />
            ))}
          </Box>
        ) : null}

        <Text align="center" color="text.disabled" variant="caption">
          {showStartScreen
            ? t('chat', 'empty_disclaimer')
            : t('chat', 'disclaimer')}
        </Text>
      </Box>

      {historySourceId ? (
        <Collapse in={browsingHistory} timeout={0}>
          <Box
            sx={{
              bgcolor: 'background.default',
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              left: 0,
              position: 'absolute',
              right: 0,
              top: 0,
              zIndex: 1
            }}
          >
            <ChatHistoryList
              chats={history}
              key={historyOpenCount}
              loading={loadingHistory}
              onBack={startNewChat}
              onSelect={resume}
            />
          </Box>
        </Collapse>
      ) : null}

      <ChatFeedbackPopover
        anchorEl={feedbackAnchor}
        onClose={closeFeedback}
        onSubmit={(reason, comment) => {
          if (feedbackFor) {
            submitRating(
              feedbackFor,
              'down',
              reason,
              comment,
              feedbackFromNudge
            )
          }

          closeFeedback()
        }}
      />
    </Box>
  )
}

export default ChatBox
